import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { Briefing } from './AdminDashboard';
import AIPreferences from '../../components/admin/AIPreferences';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const fmtPT = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { timeZone: 'America/Los_Angeles', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

const Suggestions = ({ run, headers, me, onAccepted }) => {
  const [dismissed, setDismissed] = useState([]);
  const [busy, setBusy] = useState(null);
  const [existing, setExisting] = useState(null);
  const refresh = useCallback(() => axios.get(`${API_URL}/api/admin/tasks?status=open`, { headers })
    .then((r) => setExisting(r.data.tasks.filter((t) => t.source_id === run?.id).map((t) => t.title)))
    .catch((e) => setExisting(e.response?.status === 403 ? 'forbidden' : [])), [headers, run?.id]);
  useEffect(() => { if (run) refresh(); }, [run, refresh]);
  const points = (run?.points || []).map((p, i) => (typeof p === 'string' ? { text: p, i } : { ...p, i })).filter((p) => p.text && !dismissed.includes(p.i));
  const accept = async (p) => {
    setBusy(p.i);
    try {
      await axios.post(`${API_URL}/api/admin/tasks`, { title: p.text.slice(0, 200), assigned_to: me, priority: /urgent|high/i.test(p.text) ? 'high' : 'medium',
        source_type: 'briefing', source_id: run.id, source_label: `Briefing ${run.briefing_date || (run.created_at || '').slice(0, 10)}`.trim(), description: p.link ? `Record: ${p.link}` : null }, { headers });
      toast.success('Added to Tasks');
      refresh(); onAccepted();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not add the task'); } finally { setBusy(null); }
  };
  if (!run) return <p className="text-sm text-[#77776e]" data-testid="ai-suggestions-empty">Suggestions appear once today’s briefing is ready.</p>;
  if (!points.length) return <p className="text-sm text-[#77776e]" data-testid="ai-suggestions-empty">No suggestions left for this briefing.</p>;
  const forbidden = existing === 'forbidden';
  return (
    <ul className="space-y-2" data-testid="ai-suggestions">
      {forbidden && <p className="text-xs text-[#a4471b]" data-testid="ai-suggestions-no-permission">Adding team tasks needs the task-management permission. Ask a master admin to grant it.</p>}
      {points.map((p) => {
        const already = Array.isArray(existing) && existing.includes(p.text.slice(0, 200).trim());
        return (
        <li key={p.i} className="rounded-lg border border-[#e7e4db] bg-white p-3.5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`ai-suggestion-${p.i}`}>
          <p className="flex-1 text-sm text-[#20211d]">{p.text}{p.link && <> <Link to={p.link} className="text-[#b75b28] text-xs font-semibold whitespace-nowrap">Open record ↗</Link></>}</p>
          <div className="flex gap-2">
            {already ? <span className="px-3 py-2 text-xs text-[#55704a] font-semibold" data-testid={`ai-suggestion-added-${p.i}`}>Already a task</span>
              : !forbidden && <button onClick={() => accept(p)} disabled={busy === p.i || existing === null} className="px-3 py-2 rounded-[5px] bg-[#f36a19] text-[#171912] text-xs font-bold" data-testid={`ai-suggestion-accept-${p.i}`}>Add as my task</button>}
            <button onClick={() => setDismissed((d) => [...d, p.i])} className="px-3 py-2 rounded-[5px] border border-[#dcded2] text-xs text-[#5f6455]" data-testid={`ai-suggestion-dismiss-${p.i}`}>Dismiss</button>
          </div>
        </li>
        );
      })}
    </ul>
  );
};

export default function AdminAIWorkspacePage() {
  const { token, user } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [runs, setRuns] = useState([]);
  const load = useCallback(() => axios.get(`${API_URL}/api/admin/briefing/history`, { headers }).then((r) => setRuns(r.data)).catch(() => {}), [headers]);
  useEffect(() => { load(); }, [load]);
  const latestOk = runs.find((r) => r.status === 'ok');
  const runDate = (r) => r?.briefing_date || (r?.created_at || '').slice(0, 10);
  const [tab, setTab] = useState('briefing');
  const TABS = [['briefing', 'Daily Briefing'], ['actions', 'Action Review'], ['preferences', 'Preferences'], ['history', 'Run History']];
  return (
    <main className="max-w-[1400px] mx-auto px-5 sm:px-9 py-8 space-y-8" data-testid="ai-workspace-page">
      <div>
        <p className="text-[10px] tracking-[0.16em] font-bold text-[#7a7c6d]">GLEAM GURU OPERATIONS</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-[#20211d] mt-3">AI Workspace<span className="text-[#939681] font-normal">.</span></h1>
        <p className="text-sm text-[#77776e] mt-3">A daily overview, useful suggestions, and control over every action.</p>
        <div className="flex gap-1.5 mt-7 pb-4 border-b border-[#e3e0d6] overflow-x-auto" role="tablist">
          {TABS.map(([k, l]) => (
            <button key={k} role="tab" aria-selected={tab === k} onClick={() => setTab(k)} data-testid={`ai-tab-${k}`}
              className={`px-3.5 py-2.5 rounded-[5px] text-[13px] whitespace-nowrap ${tab === k ? 'bg-[#252a20] text-white' : 'text-[#5f6455] hover:bg-[#ebe8df]'}`}>{l}</button>
          ))}
        </div>
      </div>
      {tab === 'briefing' && <>
        <p className="text-xs text-[#77776e]">Claude writes one shared briefing per Pacific day from your workspace records. Nothing is changed or sent automatically.</p>
        <Briefing headers={headers} />
      </>}
      {tab === 'actions' && <section>
        <h2 className="text-base md:text-lg font-semibold text-[#20211d]">Task suggestions</h2>
        <p className="text-xs text-[#77776e] mt-1 mb-3">From the latest briefing{latestOk ? ` (${runDate(latestOk)})` : ''}. Suggestions become tasks only when you add them; an item that already has an open task can’t be added twice.</p>
        <Suggestions run={latestOk} headers={headers} me={user?.id} onAccepted={load} />
      </section>}
      {tab === 'preferences' && <section>
        <h2 className="text-base md:text-lg font-semibold text-[#20211d]">Preferences</h2>
        <p className="text-xs text-[#77776e] mt-1 mb-3">Shared by all admins. Saved changes are recorded in the audit log.</p>
        <AIPreferences headers={headers} />
      </section>}
      {tab === 'history' && <section>
        <h2 className="text-base md:text-lg font-semibold text-[#20211d]">Run history</h2>
        <div className="mt-3 rounded-[10px] bg-white border border-[#e7e4db] divide-y divide-[#ecebe4]" data-testid="ai-run-history">
          {runs.length === 0 ? <p className="p-4 text-sm text-[#77776e]">No briefings yet.</p> : runs.map((r) => (
            <div key={r.id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-sm" data-testid={`ai-run-${r.id}`}>
              <span className="font-semibold text-[#20211d] w-28">{r.briefing_date || (r.created_at || '').slice(0, 10)}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded w-fit ${r.status === 'ok' ? 'bg-[#eef3e8] text-[#55704a]' : r.status === 'failed' ? 'bg-[#fde3d3] text-[#a4471b]' : 'bg-[#efeee6] text-[#5f6455]'}`}>{r.status.toUpperCase()}</span>
              <span className="flex-1 text-[#4d5740] truncate">{r.headline || r.error || ''}</span>
              <span className="text-xs text-[#8a8c80]">{fmtPT(r.generated_at || r.created_at)} PT · opened by {r.requested_by_name || 'Admin'}</span>
            </div>
          ))}
        </div>
      </section>}
    </main>
  );
}
